import { css } from "styled-components";
import kebabCase from "lodash.kebabcase";

const COLORS = {
  white: { DEFAULT: "#ffffff" },
  black: { DEFAULT: "#000000" },
  overlay: { DEFAULT: "rgba(0, 0, 0, 0.6)" },
  bluewood: {
    DEFAULT: "#273C49",
    50: "#F3F5F5",
    100: "#E9ECED",
    200: "#D4D8DB",
    250: "#A9B1B6",
    300: "#7D8A92",
    400: "#52636D",
    500: "#273C49",
    600: "#1F303A",
    700: "#17242C",
    800: "#10181D",
  },
  fountain: {
    DEFAULT: "#5CC6C7",
    100: "#EFF9F9",
    200: "#DEF4F4",
    300: "#BEE8E9",
    400: "#7DD1D2",
    500: "#5CC6C7",
    600: "#45BEBF",
    700: "#3BAAAB",
    800: "#339394",
    900: "#2B7D7D",
    1000: "#2B4E56", // Not officially in theme
  },
  picton: {
    DEFAULT: "#50AEEB",
    50: "#F5FBFF",
    100: "#E3F2FC",
    200: "#C7E5F9",
    300: "#87C7F1",
    400: "#6BBBEE",
    500: "#50AEEB",
    600: "#35A1E8",
    700: "#1A94E4",
    800: "#1782C8",
    900: "#1471AD",
  },
  orchid: {
    DEFAULT: "#DA72C5",
    300: "#E7A2D9",
    500: "#DA72C5",
    700: "#CD41B1",
  },
  punch: {
    DEFAULT: "#E5A34C",
    300: "#EDBE81",
    500: "#E5A34C",
    700: "#D5861F",
  },
  lime: {
    DEFAULT: "#333377",
    300: "#DFD756",
    500: "#333377",
    700: "#9D951D",
  },
  sushi: {
    DEFAULT: "#71C13A",
    100: "#E3F3D8",
    200: "#C6E6B0",
    300: "#AADA89",
    400: "#8DCD61",
    500: "#71C13A",
    600: "#5A9A2E",
    700: "#447423",
    800: "#2D4D17",
    900: "#17270C",
  },
  indigo: {
    DEFAULT: "#5656C2",
    50: "#F0F0FF",
    100: "#E1E1F4",
    200: "#B7B7E5",
    250: "#A3A3DE",
    300: "#8383D2",
    400: "#6C6CCA",
    500: "#5656C2",
    600: "#4242B7",
    700: "#3A3AA1",
    800: "#32328A",
    900: "#2A2A74",
  },
  bermuda: {
    DEFAULT: "#7ADDB6",
    100: "#E3F8F0",
    200: "#C7F0E0",
    300: "#ABE9D1",
    400: "#92E3C3",
    500: "#7ADDB6",
    600: "#62D7A9",
    700: "#49D19B",
    800: "#33C88D",
    900: "#2DB07C",
  },
  violet: {
    DEFAULT: "#4343B5",
    300: "#6C6CC9",
    500: "#4343B5",
    700: "#323288",
  },
  minsk: {
    DEFAULT: "#333377",
    300: "#4545A2",
    500: "#333377",
    700: "#21214C",
  },
  gore: {
    DEFAULT: "#24245A",
    300: "#353586",
    500: "#24245A",
    700: "#13132E",
  },
  blue: {
    DEFAULT: "#3B82F6",
    100: "#EFF5FE",
    200: "#BAD3FC",
    500: "#3B82F6",
    900: "#0847AF",
  },
  yellow: {
    DEFAULT: "#F59E0B",
    100: "#FDECCE",
    200: "#FBD89D",
    500: "#F59E0B",
    900: "#623F04",
  },
  green: {
    DEFAULT: "#10B981",
    100: "#CFF1E6",
    200: "#9FE3CD",
    500: "#10B981",
    900: "#064E3B",
  },
  red: {
    DEFAULT: "#EB5A47",
    100: "#FBDEDA",
    200: "#F7BDB5",
    300: "#F39C91",
    400: "#EF7B6C",
    500: "#EB5A47",
    600: "#BC4839",
    700: "#8D362B",
    800: "#5E241C",
    900: "#2F120E",
  },
  purple: {
    DEFAULT: "#8B5CF6",
    100: "#E8DEFD",
    200: "#D1BEFB",
    300: "#B99DFA",
    400: "#A27DF8",
    500: "#8B5CF6",
    600: "#6F4AC5",
    700: "#6F4AC5",
    800: "#382562",
    900: "#1C1231",
  },
  blush: {
    DEFAULT: "#E1718B",
    50: "#FDF0F3",
    100: "#F8DEE4",
    300: "#ECA4B4",
    400: "#E78DA2",
    500: "#E1718B",
    700: "#D63E62",
    900: "#B22646",
  },
  fuzzy: {
    DEFAULT: "#C76358",
    100: "#F6E7E5",
    200: "#e8c0bc",
    300: "#D68E86",
    500: "#C76358",
    700: "#A94439",
    900: "#7B3129",
  },
  porsche: {
    DEFAULT: "#EDAD68",
    100: "#FBEDDE",
    300: "#F4CB9F",
    400: "#F1BD86",
    500: "#EDAD68",
    700: "#E68F31",
    900: "#C37117",
  },
  confetti: {
    DEFAULT: "#EDCE68",
    100: "#FBF5E1",
    200: "#F8EBC3",
    300: "#F4E2A4",
    400: "#F1D886",
    500: "#EDCE68",
    600: "#E7BB2B",
    700: "#DBAD0D",
    900: "#C89B04",
  },
} as const;

// const STATUS_COLORS = {
//   error: "#EB5A47",
//   warning: "#ECA02F",
//   success: "#71C13A",
//   info: "#50AEEB",
//   neutral: "#273C49",
// } as const;

const ALPHAS = {
  dim: 0.6,
  ghost: 0.3,
};

// type Color = (typeof COLORS)[keyof typeof COLORS];
type ColorKey = keyof typeof COLORS;

// TODO: get ColorShadeKey from colors
type ColorShadeKey =
  | 50
  | 100
  | 200
  | 250
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900
  | 1000;

type ColorShadeValue = string; // Color[keyof Color];

export const COLOR = Object.entries(COLORS).reduce(
  (acc, [colorName, color]) => {
    if (typeof color !== "object") {
      return acc;
    }

    Object.entries(color).forEach(([shade, value]) => {
      if (shade === "DEFAULT") {
        acc[colorName as ColorKey] = value as ColorShadeValue;
        return;
      }

      acc[
        `${String(colorName)}-${String(shade)}` as `${ColorKey}-${ColorShadeKey}`
      ] = value as ColorShadeValue;
    });

    return acc;
  },
  {} as Record<ColorKey | `${ColorKey}-${ColorShadeKey}`, ColorShadeValue>,
);

export const colorsCss = css`
  ${Object.entries(COLOR).map(([key, value]) => {
    return `--color-${kebabCase(key)}: ${value};`;
  })}
`;

export const alphasCss = css`
  ${Object.entries(ALPHAS).map(([key, value]) => {
    return `--alpha-${kebabCase(key)}: ${value};`;
  })}
`;
